import React, { useState, useEffect } from 'react';
import { 
  X, 
  User, 
  Lock, 
  Mail, 
  Loader2, 
  ShieldCheck, 
  UserCircle, 
  UserPlus, 
  KeyRound, 
  ArrowLeft, 
  CheckCircle2, 
  Eye, 
  EyeOff, 
  Send 
} from 'lucide-react';
import { API_URL } from '../constants';
import { toast } from 'sonner';

const WhatsappIcon = ({ size = 20, className = "" }: { size?: number, className?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className} xmlns="http://www.w3.org/2000/svg">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
  </svg>
);

export type AuthMode = 'login' | 'register' | 'forgot_password' | 'reset_password';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLogin: (data: any) => Promise<void>;
  onRegister: (data: any) => Promise<void>;
  initialMode?: AuthMode;
  initialResetToken?: string;
}

const AuthModal: React.FC<AuthModalProps> = ({ 
  isOpen, 
  onClose, 
  onLogin, 
  onRegister,
  initialMode = 'login',
  initialResetToken = ''
}) => {
  const [mode, setMode] = useState<AuthMode>(initialMode);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Form states
  const [formData, setFormData] = useState({
    name: '',
    username: '',
    email: '',
    password: '',
    phone: '' 
  });

  // Recovery states
  const [recoverInput, setRecoverInput] = useState('');
  const [resetCode, setResetCode] = useState(initialResetToken);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setLoading(false);
      setError('');
      setSuccessMessage('');
      setMode(initialResetToken ? 'reset_password' : initialMode);
      if (initialResetToken) {
        setResetCode(initialResetToken);
      }
      setFormData(prev => ({...prev, password: ''}));
    }
  }, [isOpen, initialMode, initialResetToken]);

  if (!isOpen) return null;

  const isLogin = mode === 'login';
  const isRegister = mode === 'register';
  const isForgot = mode === 'forgot_password';
  const isReset = mode === 'reset_password';

  const switchMode = (newMode: AuthMode) => {
    setMode(newMode);
    setError('');
    setSuccessMessage('');
    setLoading(false);
    if (newMode === 'forgot_password' && formData.username && !recoverInput) {
      setRecoverInput(formData.username);
    }
  };

  const handleLoginOrRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;

    setError('');
    setSuccessMessage('');
    setLoading(true);

    try {
      if (isLogin) {
        await onLogin({ username: formData.username, password: formData.password });
      } else {
        if (!formData.name || !formData.email || !formData.password || !formData.username) {
          throw new Error("Preencha todos os campos obrigatórios.");
        }
        if (formData.password.length < 6) {
          throw new Error("A senha deve ter pelo menos 6 caracteres.");
        }
        await onRegister(formData);
      }
    } catch (err: any) {
      setError(err.message || 'Erro desconhecido na operação.');
    } finally {
      setLoading(false);
    }
  };

  const handleSendRecoveryCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;

    if (!recoverInput.trim()) {
      setError('Por favor, informe seu e-mail ou nome de usuário.');
      return;
    }

    setError('');
    setSuccessMessage('');
    setLoading(true);

    try {
      const res = await fetch(`${API_URL}?action=forgot_password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ emailOrUsername: recoverInput.trim() })
      });

      const rawText = await res.text();
      let data: any = null;
      try { data = JSON.parse(rawText); } catch (e) {}

      if (!res.ok || !data || !data.success) {
        throw new Error((data && data.error) || 'Falha ao processar a solicitação de recuperação.');
      }

      setSuccessMessage(data.message || 'Código de recuperação enviado para o seu e-mail!');
      toast.success(data.message || 'Código de recuperação enviado com sucesso!');
      
      // Muda automaticamente para a tela de digitar o código e a nova senha
      setTimeout(() => {
        setMode('reset_password');
      }, 1200);

    } catch (err: any) {
      setError(err.message || 'Erro ao enviar código de recuperação.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;

    if (!resetCode.trim()) {
      setError('Por favor, digite o código de 6 dígitos ou link recebido por e-mail.');
      return;
    }

    if (!newPassword) {
      setError('Por favor, informe a nova senha.');
      return;
    }

    if (newPassword.length < 6) {
      setError('A nova senha deve ter no mínimo 6 caracteres.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('As senhas digitadas não coincidem.');
      return;
    }

    setError('');
    setSuccessMessage('');
    setLoading(true);

    try {
      const res = await fetch(`${API_URL}?action=reset_password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code: resetCode.trim(),
          newPassword: newPassword.trim(),
          emailOrUsername: recoverInput.trim()
        })
      });

      const rawText = await res.text();
      let data: any = null;
      try { data = JSON.parse(rawText); } catch (e) {}

      if (!res.ok || !data || !data.success) {
        throw new Error((data && data.error) || 'Código inválido ou expirado.');
      }

      toast.success('Senha redefinida com sucesso! Você já pode fazer login.');
      setSuccessMessage('Senha atualizada com sucesso!');
      
      // Retorna ao login com o username preenchido
      if (data.username) {
        setFormData(prev => ({ ...prev, username: data.username, password: '' }));
      } else if (recoverInput) {
        setFormData(prev => ({ ...prev, username: recoverInput, password: '' }));
      }

      setTimeout(() => {
        setMode('login');
        setResetCode('');
        setNewPassword('');
        setConfirmPassword('');
      }, 1500);

    } catch (err: any) {
      setError(err.message || 'Erro ao redefinir a senha.');
    } finally {
      setLoading(false);
    }
  };

  const getHeaderColor = () => {
    if (isLogin) return 'bg-blue-600';
    if (isRegister) return 'bg-emerald-600';
    if (isForgot) return 'bg-amber-600';
    return 'bg-purple-600';
  };

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/70 backdrop-blur-md p-4 animate-fade-in">
      <div className="bg-white rounded-[32px] shadow-2xl w-full max-w-sm relative overflow-hidden animate-fade-in-up border border-gray-100 flex flex-col max-h-[90vh]">
        
        {/* Top Accent Bar */}
        <div className={`h-1.5 w-full shrink-0 transition-colors duration-500 ease-in-out ${getHeaderColor()}`}></div>

        {/* Close Button */}
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-900 transition-colors z-50 p-1.5 bg-gray-50 rounded-full"
        >
          <X size={18} />
        </button>

        <div className="overflow-y-auto custom-scrollbar flex-1 p-5 md:p-8">
          
          {/* Header */}
          <div className="flex flex-col items-center mb-5">
            <div className={`w-12 h-12 rounded-[18px] flex items-center justify-center mb-3 transition-all duration-500 transform hover:rotate-3 ${
              isLogin ? 'bg-blue-50 text-blue-600' :
              isRegister ? 'bg-emerald-50 text-emerald-600' :
              isForgot ? 'bg-amber-50 text-amber-600' :
              'bg-purple-50 text-purple-600'
            }`}>
              {isLogin && <UserCircle size={28} />}
              {isRegister && <UserPlus size={28} />}
              {isForgot && <KeyRound size={26} />}
              {isReset && <ShieldCheck size={28} />}
            </div>

            <h2 className="text-xl md:text-2xl font-black text-gray-900 tracking-tight text-center leading-none">
              {isLogin && 'Bem-vindo!'}
              {isRegister && 'Criar Conta'}
              {isForgot && 'Recuperar Senha'}
              {isReset && 'Nova Senha'}
            </h2>

            <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mt-1.5 text-center">
              {isLogin && 'Acesso ao Ecossistema'}
              {isRegister && 'Cadastre-se grátis'}
              {isForgot && 'Admin ou Usuário cadastrado'}
              {isReset && 'Digite o código recebido por e-mail'}
            </p>
          </div>

          {/* Mode Switcher: Only for Login and Register */}
          {(isLogin || isRegister) && (
            <div className="flex bg-gray-100/50 p-1 rounded-2xl mb-5">
              <button 
                type="button"
                onClick={() => switchMode('login')} 
                className={`flex-1 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${
                  isLogin ? 'bg-white shadow-sm text-blue-600' : 'text-gray-400 hover:text-gray-600'
                }`}
              >
                Entrar
              </button>
              <button 
                type="button"
                onClick={() => switchMode('register')} 
                className={`flex-1 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${
                  isRegister ? 'bg-white shadow-sm text-emerald-600' : 'text-gray-400 hover:text-gray-600'
                }`}
              >
                Cadastrar
              </button>
            </div>
          )}

          {/* FORM: LOGIN & REGISTER */}
          {(isLogin || isRegister) && (
            <form onSubmit={handleLoginOrRegister} className="space-y-3">
              {isRegister && (
                <div className="animate-fade-in space-y-3">
                  <div className="relative group">
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-emerald-500 transition-colors" size={14} />
                    <input 
                      type="text" 
                      value={formData.name} 
                      onChange={e => setFormData({...formData, name: e.target.value})} 
                      className="w-full bg-gray-50 text-gray-900 border border-gray-100 rounded-xl pl-10 pr-4 py-2.5 focus:border-emerald-500 focus:bg-white outline-none font-bold text-xs transition-all" 
                      placeholder="Nome Completo" 
                      required 
                    />
                  </div>
                  <div className="relative group">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-emerald-500 transition-colors" size={14} />
                    <input 
                      type="email" 
                      value={formData.email} 
                      onChange={e => setFormData({...formData, email: e.target.value})} 
                      className="w-full bg-gray-50 text-gray-900 border border-gray-100 rounded-xl pl-10 pr-4 py-2.5 focus:border-emerald-500 focus:bg-white outline-none font-bold text-xs transition-all" 
                      placeholder="E-mail" 
                      required 
                    />
                  </div>
                  <div className="relative group">
                    <div className="absolute left-4 top-1/2 -translate-y-1/2 text-emerald-500 flex items-center justify-center">
                      <WhatsappIcon size={14} />
                    </div>
                    <input 
                      type="tel" 
                      value={formData.phone} 
                      onChange={e => setFormData({...formData, phone: e.target.value})} 
                      className="w-full bg-gray-50 text-gray-900 border border-gray-100 rounded-xl pl-10 pr-4 py-2.5 focus:border-emerald-500 focus:bg-white outline-none font-bold text-xs transition-all" 
                      placeholder="WhatsApp (Opcional)" 
                    />
                  </div>
                </div>
              )}

              <div className="relative group">
                <UserCircle className={`absolute left-4 top-1/2 -translate-y-1/2 text-gray-300 transition-colors ${
                  isLogin ? 'group-focus-within:text-blue-500' : 'group-focus-within:text-emerald-500'
                }`} size={14} />
                <input 
                  type="text" 
                  value={formData.username} 
                  onChange={e => setFormData({...formData, username: e.target.value})} 
                  className={`w-full bg-gray-50 text-gray-900 border border-gray-100 rounded-xl pl-10 pr-4 py-2.5 outline-none font-bold text-xs transition-all focus:bg-white ${
                    isLogin ? 'focus:border-blue-500' : 'focus:border-emerald-500'
                  }`} 
                  placeholder="Usuário (Login)" 
                  required 
                />
              </div>
              
              <div className="relative group">
                <Lock className={`absolute left-4 top-1/2 -translate-y-1/2 text-gray-300 transition-colors ${
                  isLogin ? 'group-focus-within:text-blue-500' : 'group-focus-within:text-emerald-500'
                }`} size={14} />
                <input 
                  type="password" 
                  value={formData.password} 
                  onChange={e => setFormData({...formData, password: e.target.value})} 
                  className={`w-full bg-gray-50 text-gray-900 border border-gray-100 rounded-xl pl-10 pr-4 py-2.5 outline-none font-bold text-xs transition-all focus:bg-white ${
                    isLogin ? 'focus:border-blue-500' : 'focus:border-emerald-500'
                  }`} 
                  placeholder="Senha" 
                  required 
                />
              </div>

              {isLogin && (
                <div className="flex justify-end pt-1">
                  <button
                    type="button"
                    id="forgot-password-link"
                    onClick={() => switchMode('forgot_password')}
                    className="text-[11px] font-bold text-blue-600 hover:text-blue-700 hover:underline transition-all"
                  >
                    Esqueceu sua senha?
                  </button>
                </div>
              )}

              {error && (
                <div className="text-red-500 text-[10px] font-bold bg-red-50 p-2.5 rounded-xl text-center border border-red-100 animate-fade-in">
                  {error}
                </div>
              )}

              <button 
                type="submit" 
                disabled={loading} 
                className={`w-full font-black py-3.5 rounded-xl transition-all shadow-lg flex items-center justify-center gap-2 mt-2 transform active:scale-95 disabled:opacity-70 text-[10px] uppercase tracking-widest disabled:cursor-not-allowed ${
                  isLogin ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-blue-500/20' : 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-emerald-500/20'
                }`}
              >
                {loading ? <Loader2 className="animate-spin" size={16} /> : (isLogin ? 'Entrar Agora' : 'Finalizar Cadastro')}
              </button>
            </form>
          )}

          {/* FORM: FORGOT PASSWORD (REQUEST CODE) */}
          {isForgot && (
            <form onSubmit={handleSendRecoveryCode} className="space-y-4 animate-fade-in">
              <div className="p-3 bg-amber-50/80 rounded-2xl border border-amber-100 text-[11px] text-amber-900 leading-relaxed">
                Digite o seu <strong>nome de usuário</strong> (ex: <code>admin</code>) ou seu <strong>e-mail cadastrado</strong>. O sistema enviará um código de 6 dígitos via SMTP.
              </div>

              <div className="relative group">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-amber-500 transition-colors" size={14} />
                <input 
                  type="text" 
                  value={recoverInput} 
                  onChange={e => setRecoverInput(e.target.value)} 
                  className="w-full bg-gray-50 text-gray-900 border border-gray-100 rounded-xl pl-10 pr-4 py-3 focus:border-amber-500 focus:bg-white outline-none font-bold text-xs transition-all" 
                  placeholder="E-mail ou Usuário (ex: admin)" 
                  required 
                  autoFocus
                />
              </div>

              {error && (
                <div className="text-red-500 text-[10px] font-bold bg-red-50 p-2.5 rounded-xl text-center border border-red-100 animate-fade-in">
                  {error}
                </div>
              )}

              {successMessage && (
                <div className="text-emerald-700 text-[10px] font-bold bg-emerald-50 p-2.5 rounded-xl text-center border border-emerald-200 flex items-center justify-center gap-1.5 animate-fade-in">
                  <CheckCircle2 size={14} className="text-emerald-600 shrink-0" />
                  <span>{successMessage}</span>
                </div>
              )}

              <button 
                type="submit" 
                disabled={loading} 
                className="w-full font-black py-3.5 rounded-xl bg-amber-600 hover:bg-amber-700 text-white transition-all shadow-lg shadow-amber-600/20 flex items-center justify-center gap-2 transform active:scale-95 disabled:opacity-70 text-[10px] uppercase tracking-widest disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <Loader2 className="animate-spin" size={16} />
                    <span>Disparando E-mail...</span>
                  </>
                ) : (
                  <>
                    <Send size={14} />
                    <span>Enviar Código de Recuperação</span>
                  </>
                )}
              </button>

              <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => switchMode('login')}
                  className="text-xs font-bold text-gray-500 hover:text-gray-900 flex items-center gap-1 transition-colors"
                >
                  <ArrowLeft size={14} />
                  <span>Voltar ao Login</span>
                </button>

                <button
                  type="button"
                  onClick={() => switchMode('reset_password')}
                  className="text-[11px] font-bold text-amber-600 hover:text-amber-800 transition-colors"
                >
                  Já tenho o código
                </button>
              </div>
            </form>
          )}

          {/* FORM: RESET PASSWORD (ENTER CODE & NEW PASSWORD) */}
          {isReset && (
            <form onSubmit={handleResetPassword} className="space-y-3.5 animate-fade-in">
              <div className="p-3 bg-purple-50/80 rounded-2xl border border-purple-100 text-[11px] text-purple-900 leading-relaxed">
                Digite o código numérico de 6 dígitos que você recebeu e defina sua nova senha.
              </div>

              {/* Code Input */}
              <div className="space-y-1">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-wider">
                  Código de 6 dígitos recebido:
                </label>
                <div className="relative group">
                  <KeyRound className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-purple-500 transition-colors" size={14} />
                  <input 
                    type="text" 
                    value={resetCode} 
                    onChange={e => setResetCode(e.target.value.trim())} 
                    className="w-full bg-gray-50 text-gray-900 border border-gray-200 rounded-xl pl-10 pr-4 py-2.5 focus:border-purple-500 focus:bg-white outline-none font-mono font-bold text-sm tracking-widest text-center transition-all" 
                    placeholder="123456" 
                    required 
                    maxLength={32}
                    autoFocus
                  />
                </div>
              </div>

              {/* New Password */}
              <div className="space-y-1">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-wider">
                  Nova Senha:
                </label>
                <div className="relative group">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-purple-500 transition-colors" size={14} />
                  <input 
                    type={showNewPassword ? 'text' : 'password'} 
                    value={newPassword} 
                    onChange={e => setNewPassword(e.target.value)} 
                    className="w-full bg-gray-50 text-gray-900 border border-gray-100 rounded-xl pl-10 pr-10 py-2.5 focus:border-purple-500 focus:bg-white outline-none font-bold text-xs transition-all" 
                    placeholder="Mínimo 6 caracteres" 
                    required 
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
                  >
                    {showNewPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
              </div>

              {/* Confirm Password */}
              <div className="space-y-1">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-wider">
                  Confirmar Nova Senha:
                </label>
                <div className="relative group">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-purple-500 transition-colors" size={14} />
                  <input 
                    type={showNewPassword ? 'text' : 'password'} 
                    value={confirmPassword} 
                    onChange={e => setConfirmPassword(e.target.value)} 
                    className="w-full bg-gray-50 text-gray-900 border border-gray-100 rounded-xl pl-10 pr-4 py-2.5 focus:border-purple-500 focus:bg-white outline-none font-bold text-xs transition-all" 
                    placeholder="Repita a nova senha" 
                    required 
                  />
                </div>
              </div>

              {error && (
                <div className="text-red-500 text-[10px] font-bold bg-red-50 p-2.5 rounded-xl text-center border border-red-100 animate-fade-in">
                  {error}
                </div>
              )}

              {successMessage && (
                <div className="text-emerald-700 text-[10px] font-bold bg-emerald-50 p-2.5 rounded-xl text-center border border-emerald-200 flex items-center justify-center gap-1.5 animate-fade-in">
                  <CheckCircle2 size={14} className="text-emerald-600 shrink-0" />
                  <span>{successMessage}</span>
                </div>
              )}

              <button 
                type="submit" 
                disabled={loading} 
                className="w-full font-black py-3.5 rounded-xl bg-purple-600 hover:bg-purple-700 text-white transition-all shadow-lg shadow-purple-600/20 flex items-center justify-center gap-2 transform active:scale-95 disabled:opacity-70 text-[10px] uppercase tracking-widest disabled:cursor-not-allowed mt-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="animate-spin" size={16} />
                    <span>Salvando Nova Senha...</span>
                  </>
                ) : (
                  <>
                    <ShieldCheck size={16} />
                    <span>Redefinir e Salvar Senha</span>
                  </>
                )}
              </button>

              <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => switchMode('login')}
                  className="text-xs font-bold text-gray-500 hover:text-gray-900 flex items-center gap-1 transition-colors"
                >
                  <ArrowLeft size={14} />
                  <span>Voltar ao Login</span>
                </button>

                <button
                  type="button"
                  onClick={() => switchMode('forgot_password')}
                  className="text-[11px] font-bold text-purple-600 hover:text-purple-800 transition-colors"
                >
                  Reenviar código
                </button>
              </div>
            </form>
          )}

          <p className="mt-6 text-[8px] text-gray-400 text-center font-bold uppercase tracking-widest leading-relaxed">
            Protegido por criptografia e autenticação segura.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AuthModal;
